import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Car, CheckCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui";
import { cn } from "@/lib/utils";
import { SALES_PIPELINE_STAGE_IDS } from "./salesPipelineStages";
import { apiService } from "../../services/api";
import type { Sale } from "../../types";

function formatSaleOptionLabel(sale: Sale): string {
  const cust = sale.customer?.name || "—";
  const v = sale.vehicle;
  const veh = v ? `${v.brand?.name || ""} ${v.model?.name || ""}`.trim() || "—" : "—";
  return `${cust} · ${veh}`;
}

type SalesPipelineTabContentProps = {
  active?: boolean;
};

export const SalesPipelineTabContent: React.FC<SalesPipelineTabContentProps> = ({
  active = true,
}) => {
  const { t } = useTranslation();
  const [sales, setSales] = useState<Sale[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [detail, setDetail] = useState<Sale | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const lastIndex = SALES_PIPELINE_STAGE_IDS.length - 1;

  useEffect(() => {
    if (!active) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setListLoading(true);
        setListError(false);
        const res = await apiService.getSales({ page: 1, limit: 100 });
        if (!cancelled) {
          setSales(Array.isArray(res.sales) ? res.sales : []);
        }
      } catch {
        if (!cancelled) {
          setListError(true);
          setSales([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setDetailLoading(true);
        setDetailError(false);
        const data = await apiService.getSaleById(selectedId);
        const payload = (data as { data?: Sale }).data ?? (data as Sale);
        if (!cancelled) setDetail(payload);
      } catch {
        if (!cancelled) {
          setDetailError(true);
          setDetail(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const activeIndex = useMemo(() => {
    if (!detail?.pipelineStage) return -1;
    const idx = detail.pipelineStage.index;
    if (idx < 0 || idx > lastIndex) return -1;
    return idx;
  }, [detail, lastIndex]);

  const isCancelled = detail?.status === "cancelled";

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <p className="text-sm text-muted-foreground">{t("salesPipeline.subtitle")}</p>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("salesPipeline.selectSale")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {listError ? (
            <p className="text-sm text-destructive">{t("salesPipeline.loadSalesError")}</p>
          ) : (
            <>
              <Label htmlFor="sales-pipeline-sale">{t("salesPipeline.selectSale")}</Label>
              <Select
                value={selectedId || "__none__"}
                onValueChange={(v) => setSelectedId(v === "__none__" ? "" : v)}
                disabled={listLoading}
              >
                <SelectTrigger id="sales-pipeline-sale" className="max-w-xl">
                  <SelectValue placeholder={t("salesPipeline.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("salesPipeline.selectPlaceholder")}</SelectItem>
                  {sales.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {formatSaleOptionLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </CardContent>
      </Card>

      {selectedId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("salesPipeline.currentStageTitle")}</CardTitle>
            {detail && !detailLoading && (
              <p className="text-sm text-muted-foreground font-normal">
                {formatSaleOptionLabel(detail)}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {detailLoading && <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>}
            {detailError && <p className="text-sm text-destructive">{t("salesPipeline.loadSaleError")}</p>}
            {!detailLoading && !detailError && detail && isCancelled && (
              <p className="text-sm text-amber-700 dark:text-amber-400">{t("salesPipeline.cancelledSale")}</p>
            )}
            {!detailLoading && !detailError && detail && !isCancelled && (
              <div
                className="overflow-x-auto pb-2 -mx-2 px-2"
                role="list"
                aria-label={t("salesPipeline.currentStageTitle")}
              >
                <div className="flex items-center min-w-[920px] py-2">
                  {SALES_PIPELINE_STAGE_IDS.map((stageId, i) => {
                    const isActive = activeIndex === i;
                    const isDone = activeIndex >= 0 && i < activeIndex;
                    return (
                      <React.Fragment key={stageId}>
                        <div
                          role="listitem"
                          className="flex flex-col items-center shrink-0 w-[76px] sm:w-[84px] z-10"
                          aria-current={isActive ? "step" : undefined}
                        >
                          <div
                            className={cn(
                              "flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full border-2 transition-all duration-300",
                              isDone &&
                                "border-green-600 bg-green-50 dark:bg-green-950/40 dark:border-green-500",
                              isActive &&
                                "border-accent bg-accent/10 ring-2 ring-accent/25 dark:bg-accent/15",
                              !isDone &&
                                !isActive &&
                                "border-border bg-muted/30"
                            )}
                          >
                            {isActive ? (
                              <Car className="h-5 w-5 sm:h-6 sm:w-6 text-accent" aria-hidden />
                            ) : isDone ? (
                              <CheckCircle
                                className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400"
                                aria-hidden
                              />
                            ) : (
                              <span className="text-xs font-semibold text-muted-foreground">{i + 1}</span>
                            )}
                          </div>
                          <span className="text-[10px] sm:text-xs text-center mt-2 leading-tight text-foreground px-0.5">
                            {t(`salesPipeline.stages.${stageId}`)}
                          </span>
                        </div>
                        {i < lastIndex ? (
                          <div
                            className={cn(
                              "flex-1 h-1 min-w-[6px] max-w-[40px] sm:max-w-[56px] self-start mt-[22px] sm:mt-6 rounded-full transition-colors duration-300",
                              activeIndex >= 0 && i < activeIndex
                                ? "bg-green-600 dark:bg-green-500"
                                : "bg-border"
                            )}
                            aria-hidden
                          />
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("salesPipeline.stagesExplainTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            {SALES_PIPELINE_STAGE_IDS.map((stageId) => (
              <div
                key={stageId}
                className="rounded-lg border border-border bg-muted/20 dark:bg-muted/10 p-4"
              >
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  {t(`salesPipeline.stages.${stageId}`)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`salesPipeline.stageHelp.${stageId}`)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesPipelineTabContent;
